import z from 'zod';

import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';
import { filteredArraySchema } from 'app-components/utils/zodHelpers';

import { InvoiceItemSchema, type InvoiceItemType } from './invoiceItemTypes';

const RemoteInvoiceItemSchema = InvoiceItemSchema.and(z.object({ id: z.string() }));

const GetInvoiceItemsResponseSchema = z.object({
  resources: filteredArraySchema(RemoteInvoiceItemSchema),
  metaData: z
    .object({
      nextLink: z.string().optional(),
    })
    .optional(),
});

const PostInvoiceItemsResponseSchema = z.object({
  resource: RemoteInvoiceItemSchema,
  clientId: z.string().optional(),
  id: z.string(),
});
export type PostInvoiceItemsResponseType = z.infer<typeof PostInvoiceItemsResponseSchema>;

const PutInvoiceItemsResponseSchema = z.object({
  resource: RemoteInvoiceItemSchema,
  id: z.string(),
});
export type PutInvoiceItemsResponseType = z.infer<typeof PutInvoiceItemsResponseSchema>;

const BatchCreateInvoiceItemsResponseSchema = z.object({
  items: z.array(
    z.object({
      code: z.number(),
      resource: z.object({
        id: z.string().optional(),
        status: z.string().optional(),
        resource: RemoteInvoiceItemSchema.optional(),
      }),
      errors: z
        .array(
          z.object({
            extData: z
              .object({
                exception: z.string().optional(),
              })
              .optional(),
          }),
        )
        .optional(),
    }),
  ),
});

export default {
  async create(item: InvoiceItemType & { clientId: string }) {
    const requestUrl = `${getServiceUrl()}/businesses/items`;
    return PostInvoiceItemsResponseSchema.parse((await httpClientInterface.send('POST', requestUrl, item)).data);
  },
  async fetch() {
    const baseUrl = getServiceUrl();
    let requestUrl: string | undefined = `${baseUrl}/businesses/items`;
    const allResources: z.infer<typeof RemoteInvoiceItemSchema>[] = [];

    while (requestUrl) {
      const response = GetInvoiceItemsResponseSchema.parse((await httpClientInterface.send('GET', requestUrl)).data);
      allResources.push(...response.resources);
      requestUrl = response.metaData?.nextLink ? new URL(response.metaData.nextLink, baseUrl).href : undefined;
    }

    return allResources;
  },
  async update(item: InvoiceItemType & { id: string }) {
    const requestUrl = `${getServiceUrl()}/businesses/items/${item.id}`;
    return PutInvoiceItemsResponseSchema.parse((await httpClientInterface.send('PUT', requestUrl, item)).data);
  },
  async batchCreate(items: InvoiceItemType[]) {
    const requestUrl = `${getServiceUrl()}/businesses/items`;
    return BatchCreateInvoiceItemsResponseSchema.parse(
      (await httpClientInterface.send('PUT', requestUrl, { resources: items })).data,
    );
  },
  async delete(id: string) {
    const requestUrl = `${getServiceUrl()}/businesses/items/${id}`;
    await httpClientInterface.send('DELETE', requestUrl);
  },
} as const;
